import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Stack,
  Grid,
  CircularProgress,
  Box,
  Autocomplete,
  TextField,
  alpha,
} from "@mui/material";
import { motion } from "framer-motion";
import HomeLayout from "../../asset/layout/HomeLayout";
import { Add } from "@mui/icons-material";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import PauseCircleOutlineOutlinedIcon from "@mui/icons-material/PauseCircleOutlineOutlined";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import CustomButton from "../../components/mui/CustomButton";
import RModal from "../../components/mui/RModal";
import InputField1 from "../../components/mui/InputField1";
import { enqueueSnackbar } from "notistack";
import ResponsiveDatePickers from "../../components/mui/PichDate";
import { useDispatch, useSelector } from "react-redux";

import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useNavigate } from "react-router-dom";
import DropDownMenu from "../../components/mui/DropDownMenu";
import SelectDropdown from "../../components/mui/SelectDropdown";

import { FaEye } from "react-icons/fa";
import SearchBar from "../../components/mui/SearchBar";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import {
  UpdateTheTicket,
  deleteTicket,
  getAllTicket,
  postTheTicket,
} from "../../actions/ticketAuth";
import {
  getAllUsers,
  getAllUsersClient,
  getAllticketGetClient,
} from "../../actions/userAuth";
import AddTicketModal from "../../components/AddTicketModal";
import ViewTicketModal from "../../components/ViewTIcketModal";
import { getAllAdminUsers } from "../../actions/backEndUserAuth";

const columns = [
  "Ticket",
  "Assigned to",
  "Created on",
  "Created by",
  "Priority",
  "Type",
  "Status",
  "Actions",
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.04, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PanelCard = ({ children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: "24px",
      border: "1px solid",
      borderColor: alpha("#11409B", 0.08),
      bgcolor: "#fff",
      boxShadow: "0 4px 32px rgba(15, 23, 42, 0.06)",
      overflow: "hidden",
      ...sx,
    }}
  >
    {children}
  </Card>
);

const StatCard = ({ icon: Icon, label, value, accent, gradient }) => (
  <PanelCard sx={{ height: "100%" }}>
    <Box sx={{ p: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "14px",
            background: gradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 8px 24px ${alpha(accent, 0.35)}`,
          }}
        >
          <Icon sx={{ color: "#fff", fontSize: 24 }} />
        </Box>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: "#64748b",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              fontSize: "0.7rem",
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "1.75rem",
              color: "#0f172a",
              letterSpacing: "-0.03em",
              lineHeight: 1.1,
            }}
          >
            {value ?? "—"}
          </Typography>
        </Box>
      </Stack>
    </Box>
  </PanelCard>
);

const getPriorityStyles = (priority) => {
  const p = String(priority || "").toLowerCase();
  if (p === "high") return { color: "#dc2626", bg: alpha("#dc2626", 0.12) };
  if (p === "moderate") return { color: "#d97706", bg: alpha("#d97706", 0.12) };
  if (p === "low") return { color: "#16a34a", bg: alpha("#16a34a", 0.12) };
  return { color: "#64748b", bg: alpha("#64748b", 0.12) };
};

const formatStatusLabel = (status) => {
  const value = String(status || '').toUpperCase().replace(/\s+/g, '_');
  const labels = {
    OPEN: 'Open',
    CLOSED: 'Closed',
    IN_PROGRESS: 'In Progress',
    RESOLVED: 'Resolved',
    CLOSE: 'Closed',
    HOLD: 'In Progress',
    ON_HOLD: 'In Progress',
  };
  return labels[value] || status || '—';
};

const getStatusStyles = (status) => {
  const s = String(status || '').toUpperCase().replace(/\s+/g, '_');
  if (s === 'OPEN') return { color: '#2563eb', bg: alpha('#2563eb', 0.12) };
  if (s === 'CLOSED' || s === 'CLOSE')
    return { color: '#16a34a', bg: alpha('#16a34a', 0.12) };
  if (s === 'IN_PROGRESS' || s === 'HOLD' || s === 'ON_HOLD')
    return { color: '#d97706', bg: alpha('#d97706', 0.12) };
  if (s === 'RESOLVED')
    return { color: '#7c3aed', bg: alpha('#7c3aed', 0.12) };
  return { color: '#64748b', bg: alpha('#64748b', 0.12) };
};

const StatusChip = ({ status }) => {
  const styles = getStatusStyles(status);
  const label = formatStatusLabel(status);
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: '0.7rem',
        color: styles.color,
        bgcolor: styles.bg,
        border: `1px solid ${alpha(styles.color, 0.2)}`,
      }}
    />
  );
};

const PriorityChip = ({ priority }) => {
  const styles = getPriorityStyles(priority);
  return (
    <Chip
      label={priority || "—"}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: "0.7rem",
        color: styles.color,
        bgcolor: styles.bg,
        border: `1px solid ${alpha(styles.color, 0.2)}`,
        textTransform: "capitalize",
      }}
    />
  );
};

const TicketStatus = ['RESOLVED', 'OPEN', 'CLOSED', 'IN_PROGRESS'];
const PriorityStatus = ["high", "low", "moderate"];

export const TicketManagement = () => {
  const [open, setOpen] = useState(false);
  const [openSecondModal, setOpenSecondModal] = useState(false);
  const [ticketGet, setticketGet] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(10);
  const [allBackendUser, setAllBackendUser] = useState(10);

  const { user } = useSelector((state) => ({ ...state }));
  const { allUser } = useSelector((state) => state);

  const allUserPhoneNumbers = [];

  for (const userId in allUser) {
    const user = allUser[userId];
    if (user && user.phone) {
      allUserPhoneNumbers.push(user.id);
    }
  }

  const dropdownRef = useRef(null);
  const [value, setValue] = useState("");
  const [range, setRange] = useState([1, 30]);
  const [ticketName, setTicketName] = useState("");
  const [ticketDescription, setTicketDescription] = useState("");
  const [assignedToAdd, setAssignedToAdd] = useState([]);
  const [raisedForAdd, setRaisedForAdd] = useState([]);
  const [raisedTo, setRaisedTo] = useState("");
  const [raisedToEdit, setRaisedToEdit] = useState("");
  const [ticketStatus, setTicketStatus] = useState("");
  const [allotedTime, setAllotedTime] = useState("");
  const [priority, setPriority] = useState("");
  const [commentEdit, setCommentEdit] = useState("");
  const [number, setNumber] = useState("");
  const [unit, setUnit] = useState("hr");
  const [error, setError] = useState("");
  const [ticketNameEdit, setTicketNameEdit] = useState("");
  const [ticketDescriptionEdit, setTicketDescriptionEdit] = useState("");
  const [assignedToAddEdit, setAssignedToAddEdit] = useState([]);
  const [ticketStatusEdit, setTicketStatusEdit] = useState("");
  const [allotedTimeEdit, setAllotedTimeEdit] = useState("");
  const [priorityEdit, setPriorityEdit] = useState("");
  const [numberEdit, setNumberEdit] = useState("");
  const [unitEdit, setUnitEdit] = useState("hr");
  const [comment, setComment] = useState("");

  const [errorEdit, setErrorEdit] = useState("");
  const [ticketId, setTicketId] = useState("");
  const [createdId, setCreatedId] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [totalTicket, setTotalTicket] = useState();
  const [openTicket, setopenTicket] = useState();
  const [closeTicket, setcloseTicket] = useState();
  const [backendUser, setBackendUser] = useState([]);
  const [onHold, setonHold] = useState();
  const dispatch = useDispatch();
  const Navigate = useNavigate();
  const roleDropdownRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDateFrom, setSelectedDateFrom] = useState(null);
  const [selectedDateTo, setSelectedDateTo] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      Navigate("/");
    }
  }, []);

  const userId = user.id;

  const [user_new, setUser_new] = useState([]);

  const getData = () => {
    setLoading(true);
    dispatch(
      getAllUsersClient(
        { page: 1, perPage: "" },
        (data) => {
          const filteredData = data.data.data.users.filter((item) => {});
          filteredData.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          setUser_new(data.data.data.users.filter((item) => item.role == 'USER'));

          setLoading(false);
        },
        (error) => {
          setLoading(false);
        }
      )
    );
  };

  const autoCompleteOptions = user_new.map((user) => ({
    id: user.id,
    displayValue: `${user.username || user.email || ""} (${user.phone || ""})`,
  }));

  const getTicket = () => {
    setLoading(true);
    dispatch(
      getAllTicket(
        { page: 1, perPage: 500 },
        userId,
        (data) => {
          const tickets = Array.isArray(data?.data?.data) ? data.data.data : [];
          const filteredTickets = tickets.filter((ticket) => {
            const matchQuery =
              ticket.ticketName
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase()) ||
              ticket.createdAt
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase()) ||
              ticket.createdBy?.username
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase()) ||
              (ticket.assignedTo || []).some(
                (assigned) =>
                  assigned.username
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                  assigned.phone
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase())
              ) ||
              ticket.priority
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase()) ||
              ticket.createdBy?.phone
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase()) ||
              ticket.status?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchDate =
              (!selectedDateFrom ||
                new Date(ticket.updatedAt) >= new Date(selectedDateFrom)) &&
              (!selectedDateTo ||
                new Date(ticket.updatedAt) <= new Date(selectedDateTo));

            return matchQuery && matchDate;
          });

          filteredTickets.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );

          setticketGet(filteredTickets);
          setLoading(false);
          setTotalTicket(filteredTickets.length);
          const openTickets = filteredTickets.filter(
            (ticket) => ticket.status === 'OPEN'
          );
          setopenTicket(openTickets.length);
          const closeTickets = filteredTickets.filter(
            (ticket) => ticket.status === 'CLOSED'
          );
          setcloseTicket(closeTickets.length);

          const holdTickets = filteredTickets.filter(
            (ticket) => ticket.status === 'IN_PROGRESS'
          );
          setonHold(holdTickets.length);
        },
        (err) => {
          console.log(err);
          setLoading(false);
        }
      )
    );
  };
  const getFilteredticketGet = () => {
    if (!filterValue) return ticketGet;
    return ticketGet.filter((user) =>
      (user.assignedTo || []).some(
        (assigned) =>
          assigned.username.includes(filterValue) ||
          assigned.phone.includes(filterValue)
      )
    );
  };

  const filteredticketGet = getFilteredticketGet();
  const paginatedData = filteredticketGet?.slice(
    (currentPage - 1) * countPerPage,
    currentPage * countPerPage
  );
  const fetchAdminUsers = () => {
    setLoading(true);
    dispatch(
      getAllAdminUsers(
        { page: currentPage, perPage: countPerPage },
        userId,
        (data) => {
          setLoading(false);
          setBackendUser(data.data.data.users);
        },
        (err) => {
          console.log(err);
          setLoading(false);
        }
      )
    );
  };

  useEffect(() => {
    getData();
    getTicket();
    fetchAdminUsers();
  }, [searchTerm, selectedDateFrom, selectedDateTo, dispatch]);

  const assignedTo = allUserPhoneNumbers?.map((phoneNumber) => ({
    label: phoneNumber,
  }));

  const handleOpen = (user, userId) => {
    setOpen(true);
    const ticketKey = user?.id || user?._id;
    if (user && userId === ticketKey) {
      setTicketNameEdit(user?.ticketName);
      setTicketDescriptionEdit(user?.ticketDescription);
      setAssignedToAddEdit(
        (user?.assignedTo || []).map((item) => item.id).filter(Boolean)
      );
      setRaisedToEdit(user?.raisedFor?.id || user?.raisedForUserId || "");
      setTicketStatusEdit(user?.status);
      setAllotedTimeEdit(user?.allotedTime);
      setPriorityEdit(user?.priority);
      setNumberEdit(
        user?.allottedTimeHours ?? user?.allotedTime?.number ?? ""
      );
      setUnitEdit(user?.allotedTime?.unit || "hr");
      setCreatedId(ticketKey);
      setCommentEdit(user?.comment || "");
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpenSecondModal = () => {
    setOpenSecondModal(true);
  };

  const handleCloseSecondModal = () => {
    setOpenSecondModal(false);
  };

  const formDataEdit = {
    ticketName: ticketNameEdit,
    ticketDescription: ticketDescriptionEdit,
    assignedTo: assignedToAddEdit,
    raisedFor: raisedToEdit,
    number: numberEdit,
    status: ticketStatusEdit,
    priority: priorityEdit,
    unit: unitEdit,
    comment: commentEdit,
  };

  const handleEdit = (event) => {
    event.preventDefault();
    setLoading(true);

    const updatedFormDataEdit = {
      ...formDataEdit,
    };

    dispatch(
      UpdateTheTicket(
        updatedFormDataEdit,
        userId,
        createdId,
        (response) => {
          enqueueSnackbar("Data submitted successfully", {
            variant: "success",
          });

          getTicket();

          handleClose();
          setLoading(false);
        },
        (error) => {
          enqueueSnackbar("Error submitting data", {
            variant: "error",
          });
          console.error(error);
        }
      )
    );
  };

  const handleNumberChange = (event) => {
    const value = event.target.value;
    let isValid = true;

    setNumber(value);
    setError("");
  };

  const handleUnitChange = (event) => {
    if (event && event.target) {
      setUnit(event.target.value);
      setNumber("");
      setError("");
    }
  };

  const formDataAdd = {
    ticketName: ticketName,
    ticketDescription: ticketDescription,
    assignedTo: assignedToAdd,
    raisedFor: raisedTo,
    number: number,
    status: ticketStatus || 'OPEN',
    priority: priority || "low",
    unit: unit,
    comment: comment,
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setLoading(true);
    let isValid = true;
    let errorMessage = "";

    if (unit === "day") {
      isValid = number >= 1 && number <= 30;
      errorMessage = "Please enter a value between 1 and 30 for days.";
    } else if (unit === "hr") {
      isValid = number >= 1 && number <= 8;
      errorMessage = "Please enter a value between 1 and 8 for hours.";
    } else if (unit === "min") {
      isValid = number >= 1 && number <= 60;
      errorMessage = "Please enter a value between 1 and 60 for minutes.";
    }

    if (isValid) {
      dispatch(
        postTheTicket(
          formDataAdd,
          userId,
          (response) => {
            enqueueSnackbar("Data submitted successfully", {
              variant: "success",
            });

            setLoading(false);
            setNumber("");
            handleCloseSecondModal();
            getTicket();
          },
          (error) => {
            enqueueSnackbar("Error submitting data", {
              variant: "error",
            });
            console.error(error);
          }
        )
      );
    } else {
      setError(errorMessage);
    }
  };

  const handleReset = () => {
    setTicketName("");
    setTicketDescription("");
    setAssignedToAdd([]);
    setRaisedTo([]);
    setTicketStatus("");
    setAllotedTime("");
    setPriority("");
    setNumber("");
  };

  const menuItemsAllotedTime = [
    { value: "day", label: "Day" },
    { value: "hr", label: "Hour" },
    { value: "min", label: "Minute" },
  ];

  // const handleDelete = (id, createdId) => {
  //   dispatch(
  //     deleteTicket(
  //       id,
  //       createdId,
  //       (data) => {
  //         enqueueSnackbar("Deleted Successfully", {
  //           variant: "success",
  //         });
  //         getTicket();
  //       },
  //       (err) => {
  //         enqueueSnackbar("Error Occured! Please try again", {
  //           variant: "error",
  //         });
  //       }
  //     )
  //   );
  // };

  const [viewOpen, setViewOpen] = useState(false);

  const handleOpenModalView = (ticket) => {
    setSelectedTicket(ticket);
    setViewOpen(true);
  };
  const handleCloseModalVIew = () => {
    setViewOpen(false);
    setSelectedTicket(null);
  };

  const handleSearch = (query) => {
    setSearchTerm(query);
    setCurrentPage(1);
  };

  const handleExport = () => {
    const workbook = XLSX.utils.book_new();
    const worksheetData = [
      [
        "Ticket Name",
        "Assigned To",
        "Created At",
        "Created By",
        "Priority",
        "Status",
      ],
      ...filteredticketGet?.map((ticket) => [
        ticket?.ticketName,
        ticket?.assignedTo
          .map((assigned) => assigned?.username || assigned?.phone)
          .join(", "),
        ticket.createdAt
          ? new Date(ticket?.createdAt).toLocaleDateString("en-CA")
          : "N/A",
        ticket?.createdBy?.username || ticket?.createdBy?.phone,
        ticket?.priority || "Unknown",
        ticket?.status || "Unknown",
      ]),
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Tickets");
    XLSX.writeFile(workbook, "tickets_data.xlsx");
  };

  const handleSearchClear = () => {
    setSearchTerm("");
    setSelectedDateFrom(null);
    setSelectedDateTo(null);
    setSearchQuery("");
    setCurrentPage(1);
  };

  const totalPages =
    Math.ceil((filteredticketGet?.length || 0) / countPerPage) || 1;

  const premiumButtonSx = {
    height: "44px",
    borderRadius: "12px",
    fontWeight: 600,
    textTransform: "none",
    background: "linear-gradient(135deg, #11409B 0%, #2563eb 100%)",
    boxShadow: "0 6px 24px rgba(37, 99, 235, 0.35)",
    "&:hover": {
      background: "linear-gradient(135deg, #0f3580 0%, #1d4ed8 100%)",
    },
  };

  const formatCreatedDate = (date) =>
    date ? dayjs(date).format("DD MMM YYYY") : "—";

  return (
    <HomeLayout>
      <Box
        sx={{
          minHeight: "100%",
          background:
            "linear-gradient(180deg, #f0f4fa 0%, #f5f7fa 35%, #eef2f7 100%)",
          mx: -3,
          mt: -3,
          px: 3,
          pt: 3,
          pb: 4,
        }}
      >
        <motion.div initial="hidden" animate="visible" variants={fadeUp}>
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              mb: 3,
              borderRadius: "24px",
              background:
                "linear-gradient(135deg, #0c3483 0%, #11409B 40%, #1e40af 70%, #2563eb 100%)",
              color: "#fff",
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(17, 64, 155, 0.35)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -50,
                right: -30,
                width: 200,
                height: 200,
                borderRadius: "50%",
                bgcolor: alpha("#fff", 0.06),
              }}
            />
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={2}
              sx={{ position: "relative", zIndex: 1 }}
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <ConfirmationNumberOutlinedIcon />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, letterSpacing: "0.1em", opacity: 0.85 }}
                  >
                    SUPPORT
                  </Typography>
                </Stack>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, letterSpacing: "-0.03em" }}
                >
                  Ticket management
                </Typography>
                <Typography sx={{ mt: 1, opacity: 0.82, maxWidth: 520 }}>
                  Track open, in-progress, closed, and resolved tickets. Search,
                  filter by date, export, or create new support tickets.
                </Typography>
              </Box>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <CustomButton
                  variant="contained"
                  onClick={handleExport}
                  startIcon={<FileDownloadOutlinedIcon />}
                  sx={{
                    ...premiumButtonSx,
                    bgcolor: alpha("#fff", 0.15),
                    backdropFilter: "blur(8px)",
                    border: `1px solid ${alpha("#fff", 0.25)}`,
                    boxShadow: "none",
                    "&:hover": { bgcolor: alpha("#fff", 0.22) },
                  }}
                >
                  Export tickets
                </CustomButton>
                <CustomButton
                  variant="contained"
                  onClick={handleOpenSecondModal}
                  startIcon={<Add />}
                  sx={{
                    ...premiumButtonSx,
                    bgcolor: alpha("#fff", 0.15),
                    backdropFilter: "blur(8px)",
                    border: `1px solid ${alpha("#fff", 0.25)}`,
                    boxShadow: "none",
                    "&:hover": { bgcolor: alpha("#fff", 0.22) },
                  }}
                >
                  Add ticket
                </CustomButton>
              </Stack>
            </Stack>
          </Box>
        </motion.div>

        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={0} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={ConfirmationNumberOutlinedIcon}
                label="Total tickets"
                value={totalTicket}
                accent="#4f46e5"
                gradient="linear-gradient(135deg, #818cf8 0%, #4f46e5 100%)"
              />
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={1} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={LockOpenOutlinedIcon}
                label="Open"
                value={openTicket}
                accent="#2563eb"
                gradient="linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)"
              />
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={2} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={CheckCircleOutlineOutlinedIcon}
                label="Closed"
                value={closeTicket}
                accent="#059669"
                gradient="linear-gradient(135deg, #34d399 0%, #059669 100%)"
              />
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={3} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={PauseCircleOutlineOutlinedIcon}
                label="In progress"
                value={onHold}
                accent="#d97706"
                gradient="linear-gradient(135deg, #fbbf24 0%, #d97706 100%)"
              />
            </motion.div>
          </Grid>
        </Grid>

        <motion.div custom={4} initial="hidden" animate="visible" variants={fadeUp}>
          <PanelCard sx={{ mb: 3, p: { xs: 2, md: 2.5 } }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <FilterListOutlinedIcon sx={{ color: "#11409B", fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                Filters
              </Typography>
            </Stack>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <SearchBar
                  onSearch={handleSearch}
                  searchTerm={searchTerm}
                  height="2.75rem"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <ResponsiveDatePickers
                  selectedDate={selectedDateFrom}
                  setSelectedDate={setSelectedDateFrom}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <ResponsiveDatePickers
                  selectedDate={selectedDateTo}
                  setSelectedDate={setSelectedDateTo}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomButton
                  variant="outlined"
                  onClick={handleSearchClear}
                  fullWidth
                  sx={{
                    height: "2.75rem",
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: alpha("#11409B", 0.25),
                    color: "#11409B",
                    "&:hover": {
                      borderColor: "#11409B",
                      bgcolor: alpha("#11409B", 0.04),
                    },
                  }}
                >
                  Clear
                </CustomButton>
              </Grid>
            </Grid>
          </PanelCard>
        </motion.div>

        <motion.div custom={5} initial="hidden" animate="visible" variants={fadeUp}>
          {loading ? (
            <PanelCard>
              <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
                <CircularProgress sx={{ color: "#11409B" }} />
                <Typography sx={{ mt: 2, color: "#64748b" }}>
                  Loading tickets…
                </Typography>
              </Stack>
            </PanelCard>
          ) : paginatedData?.length > 0 ? (
            <TableContainer component={PanelCard} sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: alpha("#11409B", 0.04),
                      "& th": {
                        borderBottom: `1px solid ${alpha("#11409B", 0.08)}`,
                      },
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column}
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          color: "#11409B",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          py: 2,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {column}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((ticket) => (
                    <TableRow
                      key={ticket?.id || ticket?._id}
                      sx={{
                        transition: "background 0.2s",
                        "&:hover": { bgcolor: alpha("#11409B", 0.03) },
                        "& td": {
                          borderBottom: `1px solid ${alpha("#11409B", 0.06)}`,
                          py: 1.75,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                          {ticket?.ticketName || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" flexWrap="wrap" gap={0.5} maxWidth={200}>
                          {ticket?.assignedTo?.map((item, index) => (
                            <Chip
                              key={item.id || index}
                              label={item.username || item.phone || "—"}
                              size="small"
                              sx={{
                                bgcolor: alpha("#11409B", 0.08),
                                color: "#11409B",
                                fontWeight: 600,
                                fontSize: "0.7rem",
                              }}
                            />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: "#64748b" }}>
                          {formatCreatedDate(ticket?.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: "#475569" }}>
                          {ticket?.createdBy?.username ||
                            ticket?.createdBy?.phone ||
                            "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <PriorityChip priority={ticket?.priority} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ticket?.type || "B2C"}
                          size="small"
                          sx={{
                            bgcolor: alpha("#64748b", 0.1),
                            color: "#475569",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <StatusChip status={ticket?.status} />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <IconButton
                            onClick={() =>
                              handleOpen(ticket, ticket?.id || ticket?._id)
                            }
                            size="small"
                            sx={{
                              borderRadius: "10px",
                              border: `1px solid ${alpha("#11409B", 0.15)}`,
                              "&:hover": { bgcolor: alpha("#11409B", 0.06) },
                            }}
                          >
                            <img src="/liEdit.svg" alt="Edit" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleOpenModalView(ticket)}
                            size="small"
                            sx={{
                              borderRadius: "10px",
                              border: `1px solid ${alpha("#11409B", 0.15)}`,
                              color: "#11409B",
                              "&:hover": { bgcolor: alpha("#11409B", 0.06) },
                            }}
                          >
                            <FaEye />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <PanelCard>
              <Stack alignItems="center" sx={{ py: 8, px: 3 }}>
                <ConfirmationNumberOutlinedIcon
                  sx={{ fontSize: 56, color: "#cbd5e1", mb: 2 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
                  No tickets found
                </Typography>
                <Typography sx={{ color: "#64748b", mt: 1, textAlign: "center" }}>
                  Try adjusting your search or date filters, or create a new ticket.
                </Typography>
                <CustomButton
                  variant="contained"
                  onClick={handleOpenSecondModal}
                  startIcon={<Add />}
                  sx={{ ...premiumButtonSx, mt: 3 }}
                >
                  Add ticket
                </CustomButton>
              </Stack>
            </PanelCard>
          )}
        </motion.div>

        {!loading && filteredticketGet?.length > 0 && (
          <motion.div custom={6} initial="hidden" animate="visible" variants={fadeUp}>
            <PanelCard sx={{ mt: 3, p: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Typography sx={{ color: "#64748b", fontWeight: 500 }}>
                  Showing {(currentPage - 1) * countPerPage + 1}–
                  {Math.min(currentPage * countPerPage, filteredticketGet.length)}{" "}
                  of {filteredticketGet.length}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Rows per page
                  </Typography>
                  <Box
                    component="select"
                    value={countPerPage}
                    onChange={(e) => {
                      setCountPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    sx={{
                      border: `1px solid ${alpha("#11409B", 0.2)}`,
                      borderRadius: "10px",
                      px: 1.5,
                      py: 0.75,
                      fontSize: "0.875rem",
                      color: "#0f172a",
                      bgcolor: "#fff",
                      outline: "none",
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={30}>30</option>
                  </Box>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                      sx={{
                        border: "1px solid",
                        borderColor: alpha("#11409B", 0.15),
                        borderRadius: "12px",
                        bgcolor: "#fff",
                        "&:hover": { bgcolor: alpha("#11409B", 0.06) },
                      }}
                    >
                      <KeyboardArrowLeftIcon />
                    </IconButton>
                    <Typography sx={{ fontWeight: 600, minWidth: 48, textAlign: "center" }}>
                      {currentPage} / {totalPages}
                    </Typography>
                    <IconButton
                      onClick={() =>
                        setCurrentPage((p) => Math.min(p + 1, totalPages))
                      }
                      disabled={currentPage >= totalPages}
                      sx={{
                        border: "1px solid",
                        borderColor: alpha("#11409B", 0.15),
                        borderRadius: "12px",
                        bgcolor: "#fff",
                        "&:hover": { bgcolor: alpha("#11409B", 0.06) },
                      }}
                    >
                      <KeyboardArrowRightIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              </Stack>
            </PanelCard>
          </motion.div>
        )}
      </Box>
      {viewOpen && (
        <ViewTicketModal
          open={viewOpen}
          handleClose={handleCloseModalVIew}
          ticket={selectedTicket}
        />
      )}

      {open && (
        <RModal
          open={open}
          handleClose={handleClose}
          width={{ md: "60%", xs: "80%" }}
          height={{ md: "80%", xs: "80%" }}
        >
          <form onSubmit={handleEdit}>
            <Typography variant='h4'>Update Ticket </Typography>

            <Grid container spacing={{ xl: 1, sm: 0, xs: 0 }}>
              <Grid item md={6} xs={12} sm={12}>
                <InputField1
                  placeholderColor=' #8D98AA'
                  label={"Ticket Name"}
                  placeholder={"Enter User Name"}
                  spanP={true}
                  value={ticketNameEdit}
                  onChange={(e) => setTicketNameEdit(e.target.value)}
                  required
                  maxLength={20}
                  disabled
                />
              </Grid>
              <Grid item md={6} xs={12} sm={12}>
                <InputField1
                  placeholderColor=' #8D98AA'
                  label={"Ticket Description"}
                  placeholder={"Enter Ticket Description"}
                  spanP={true}
                  value={ticketDescriptionEdit}
                  onChange={(e) => setTicketDescriptionEdit(e.target.value)}
                  required
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <Grid container spacing={2}>
                  <Grid item md={8} xs={6}>
                    <InputField1
                      placeholderColor=' #8D98AA'
                      label={"Alloted Time"}
                      placeholder={"Enter Alloted Time"}
                      spanP={true}
                      required
                      type='number'
                      value={numberEdit}
                      onChange={(e) => setNumberEdit(e.target.value)}
                      disabled={unit === ""}
                    />
                    {error && (
                      <Typography color='error' variant='body2'>
                        {error}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item md={4} xs={6}>
                    <Box mt={6}>
                      {" "}
                      <SelectDropdown
                        buttonText='Select Time Unit'
                        menuItems={menuItemsAllotedTime}
                        selectedItem={unitEdit}
                        // setSelectedItem={setUnit}
                        onChange={handleUnitChange}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item md={6} xs={12} sm={12}>
                <Typography variant='h5' my={1.5}>
                  Raised For
                </Typography>

                <Autocomplete
                  id='tags-filled'
                  options={autoCompleteOptions}
                  getOptionLabel={(option) => option.displayValue}
                  value={
                    autoCompleteOptions.find(
                      (opt) => opt.id === raisedToEdit
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setRaisedToEdit(newValue.id);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder='Raised for'
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "2.5rem",
                          background: "#FFFFFF",
                          borderRadius: "7px",
                          paddingRight: "8px",
                          py: "auto",
                        },
                        "& .MuiInputBase-input": {
                          mt: "-12px",
                        },
                        "& .MuiAutocomplete-tag": {
                          mt: "-7px !important",
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item md={6} xs={12} sm={12}>
                <Typography variant='h5' my={1.5}>
                  Assigned to
                </Typography>

                <Autocomplete
                  multiple
                  id='tags-filled'
                  options={backendUser}
                  getOptionLabel={(option) =>
                    option.name ||
                    option.username ||
                    option.email ||
                    option.phone ||
                    ""
                  }
                  value={backendUser.filter((user) =>
                    assignedToAddEdit.includes(user.id)
                  )}
                  onChange={(event, newValue) => {
                    setAssignedToAddEdit(newValue.map((user) => user.id));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder='Assigned To'
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "2.5rem",
                          background: "#FFFFFF",
                          borderRadius: "7px",
                          paddingRight: "8px",
                          py: "auto",
                        },
                        "& .MuiInputBase-input": {
                          mt: "-12px",
                        },
                        "& .MuiAutocomplete-tag": {
                          mt: "-7px !important",
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item md={6} xs={12} sm={12}>
                <Typography variant='h5' mb={2} mt={1}>
                  Ticket Status
                </Typography>
                <DropDownMenu
                  buttonText='Ticket Status'
                  menuItems={TicketStatus.map((item) => {
                    return { label: item };
                  })}
                  selectedItem={
                    ticketStatusEdit ? { label: ticketStatusEdit } : null
                  }
                  setSelectedItem={setTicketStatusEdit}
                  buttonWidth={"100%"}
                />
              </Grid>
              <Grid item md={6} xs={12} sm={12}>
                <Typography variant='h5' my={2}>
                  Priority
                </Typography>
                <DropDownMenu
                  buttonText='Priority'
                  menuItems={PriorityStatus.map((item) => {
                    return { label: item };
                  })}
                  selectedItem={priorityEdit ? { label: priorityEdit } : null}
                  setSelectedItem={setPriorityEdit}
                  buttonWidth={"100%"}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <Typography variant='h5' my={2}>
                  Comment
                </Typography>
                <TextField
                  multiline
                  rows={1}
                  fullWidth
                  placeholder='Enter comment here ......'
                  value={commentEdit}
                  onChange={(e) => setCommentEdit(e.target.value)}
                />
              </Grid>
            </Grid>
            <Stack
              direction={"row"}
              spacing={{ xl: 6, md: 6, sm: 1, xs: 1 }}
              mt={4}
            >
              {" "}
              <CustomButton
                variant='contained'
                width={{
                  xl: "20vh",
                  md: "50%",
                  sm: "50%",
                  xs: "60%",
                }}
                height={"3rem"}
                type='submit'
              >
                {loading ? <CircularProgress size={24} /> : "Update "}
              </CustomButton>
              {/* <CustomButton
                    variant='outlined'
                    width={{ xl: '20vh', md: '50%', sm: '50%', xs: '40%' }}
                    height={'3rem'}
                    type='reset'
                  >
                    Reset
                  </CustomButton> */}
            </Stack>
          </form>
        </RModal>
      )}

      {openSecondModal && (
        <RModal
          open={openSecondModal}
          handleClose={handleCloseSecondModal}
          width={{ md: "80%", xs: "80%" }}
          height={{ md: "80%", xs: "80%" }}
        >
          <form onSubmit={handleSubmit}>
            <Typography variant='h4'>Add Ticket</Typography>
            <Grid container spacing={{ xl: 1, sm: 0, xs: 0 }}>
              <Grid item xl={6} xs={12} sm={12}>
                <InputField1
                  placeholderColor=' #8D98AA'
                  label={"Ticket Name"}
                  placeholder={"Enter Ticket Name"}
                  spanP={true}
                  onChange={(e) => setTicketName(e?.target?.value)}
                  required
                  maxLength={20}
                />
              </Grid>
              <Grid item xl={6} xs={12} sm={12}>
                <InputField1
                  placeholderColor=' #8D98AA'
                  label={"Ticket Description"}
                  placeholder={"Enter Ticket Description"}
                  spanP={true}
                  onChange={(e) => setTicketDescription(e.target.value)}
                  required
                />
              </Grid>

              <Grid item xl={6} xs={12} sm={12}>
                <Grid container spacing={2}>
                  <Grid item md={8} xs={6}>
                    <InputField1
                      placeholderColor=' #8D98AA'
                      label={"Alloted Time"}
                      placeholder={"Enter Alloted Time"}
                      spanP={true}
                      type='number'
                      value={number}
                      onChange={handleNumberChange}
                      disabled={unit === ""}
                    />
                    {error && (
                      <Typography color='error' variant='body2'>
                        {error}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item md={4} xs={6}>
                    <Box mt={6}>
                      {" "}
                      <SelectDropdown
                        buttonText='Select Time Unit'
                        menuItems={menuItemsAllotedTime}
                        selectedItem={unit}
                        setSelectedItem={setUnit}
                        onChange={handleUnitChange}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xl={6} xs={12} sm={12}>
                <Typography variant='h5' my={1.5}>
                  Raised For
                </Typography>

                <Autocomplete
                  id='tags-filled'
                  options={autoCompleteOptions}
                  getOptionLabel={(option) => option.displayValue}
                  value={
                    autoCompleteOptions.find((opt) => opt.id === raisedTo) ||
                    null
                  }
                  onChange={(event, newValue) => {
                    if (!newValue) {
                      setRaisedTo(null);
                      return;
                    }
                    setRaisedTo(newValue.id);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder='Raised for'
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "2.5rem",
                          background: "#FFFFFF",
                          borderRadius: "7px",
                          paddingRight: "8px",
                          py: "auto",
                        },
                        "& .MuiInputBase-input": {
                          mt: "-12px",
                        },
                        "& .MuiAutocomplete-tag": {
                          mt: "-7px !important",
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xl={6} xs={12} sm={12}>
                <Typography variant='h5' my={1.5}>
                  Assigned to
                </Typography>

                <Autocomplete
                  multiple
                  id='tags-filled'
                  options={backendUser}
                  getOptionLabel={(option) =>
                    option.name ||
                    option.username ||
                    option.email ||
                    option.phone ||
                    ""
                  }
                  value={backendUser.filter((user) =>
                    assignedToAdd.includes(user.id)
                  )}
                  onChange={(event, newValue) => {
                    setAssignedToAdd(newValue.map((user) => user.id));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder='Assigned To'
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "2.5rem",
                          background: "#FFFFFF",
                          borderRadius: "7px",
                          paddingRight: "8px",
                          py: "auto",
                        },
                        "& .MuiInputBase-input": {
                          mt: "-12px",
                        },
                        "& .MuiAutocomplete-tag": {
                          mt: "-7px !important",
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xl={6} xs={12} sm={12}>
                <Typography variant='h5' my={2}>
                  Ticket Status
                </Typography>
                <DropDownMenu
                  buttonText='Ticket Status'
                  menuItems={TicketStatus.map((item) => {
                    return { label: item };
                  })}
                  selectedItem={ticketStatus}
                  setSelectedItem={setTicketStatus}
                  buttonWidth={"100%"}
                />
              </Grid>

              <Grid item xl={6} xs={12} sm={12}>
                <Typography variant='h5' my={2}>
                  Priority
                </Typography>
                <DropDownMenu
                  buttonText='Priority'
                  menuItems={PriorityStatus.map((item) => {
                    return { label: item };
                  })}
                  selectedItem={priority}
                  setSelectedItem={setPriority}
                  buttonWidth={"100%"}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <Typography variant='h5' my={2}>
                  Comment
                </Typography>
                <TextField
                  multiline
                  rows={1}
                  fullWidth
                  placeholder='Enter comment here ......'
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </Grid>
            </Grid>

            <Stack
              direction={"row"}
              spacing={{ xl: 6, md: 6, sm: 1, xs: 1 }}
              mt={4}
            >
              {" "}
              <CustomButton
                variant='contained'
                width={{
                  xl: "20vh",
                  md: "50%",
                  sm: "50%",
                  xs: "60%",
                }}
                height={"3rem"}
                type='submit'
              >
                {loading ? <CircularProgress size={24} /> : "Add"}
              </CustomButton>
              <CustomButton
                variant='outlined'
                width={{
                  xl: "20vh",
                  md: "50%",
                  sm: "50%",
                  xs: "40%",
                }}
                height={"3rem"}
                type='reset'
                onClick={handleReset}
              >
                Reset
              </CustomButton>
            </Stack>
          </form>
        </RModal>
      )}
    </HomeLayout>
  );
};
